// ============================================================================
// CONFIG HOOKS
// ============================================================================
// React hooks for Configuration Framework API operations

import { useState, useCallback, useEffect } from 'react';
import { configService } from 'api/services/configService';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  AdvancePolicyConfig,
  ApprovalRulesConfig,
  BlockingPolicyConfig,
} from 'api/types/config.types';

/**
 * Hook to fetch all config groups (auto-fetch on mount)
 */
export const useConfigGroups = () => {
  const [data, setData] = useState<ConfigGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getGroups();
      setData(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return { data, loading, error, refetch: fetchGroups };
};

/**
 * Hook to fetch config definitions for a group (on-demand)
 */
export const useConfigDefinitions = () => {
  const [data, setData] = useState<ConfigDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDefinitions = useCallback(
    async (groupKey: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await configService.getDefinitions(groupKey);
        setData(response.data);
        return response.data;
      } catch (err) {
        const error = err as Error;
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { data, loading, error, fetchDefinitions };
};

/**
 * Hook to fetch effective config for a group with scope resolution (on-demand)
 */
export const useEffectiveConfig = () => {
  const [data, setData] = useState<ConfigEffectiveValue[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfig = useCallback(
    async (
      group: string,
      scopes?: Array<{ type: string; id: string }>,
      options?: {
        onSuccess?: (data: ConfigEffectiveValue[]) => void;
        onError?: (error: Error) => void;
      }
    ) => {
      setLoading(true);
      setError(null);
      try {
        const response = await configService.getEffectiveConfig(group, scopes);
        setData(response.data);
        options?.onSuccess?.(response.data);
        return response.data;
      } catch (err) {
        const error = err as Error;
        setError(error);
        options?.onError?.(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { data, loading, error, fetchConfig };
};

/**
 * Hook to fetch advance policy config for a property (and optionally a rate plan).
 * Auto-fetches when propertyId changes. Returns typed AdvancePolicyConfig.
 */
export const useAdvancePolicy = (
  propertyId?: string,
  ratePlanId?: string
) => {
  const [config, setConfig] = useState<AdvancePolicyConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAdvancePolicy = useCallback(
    async (propId: string, rpId?: string) => {
      setLoading(true);
      setError(null);
      try {
        const scopes: Array<{ type: string; id: string }> = [
          { type: 'PROPERTY', id: propId },
        ];
        if (rpId) {
          scopes.push({ type: 'RATE_PLAN', id: rpId });
        }

        const response = await configService.getEffectiveConfig(
          'ADVANCE_POLICY',
          scopes
        );
        const values = response.data;

        // Extract typed values from effective config
        const percentVal = values.find(
          (v) => v.configKey === 'advance_required_percent'
        );
        const daysVal = values.find(
          (v) => v.configKey === 'advance_due_days'
        );

        const result: AdvancePolicyConfig = {
          advancePercent: percentVal?.effectiveValue
            ? parseFloat(percentVal.effectiveValue)
            : 50,
          advanceDueDays: daysVal?.effectiveValue
            ? parseInt(daysVal.effectiveValue, 10)
            : 7,
        };

        setConfig(result);
        return result;
      } catch (err) {
        const error = err as Error;
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Auto-fetch when propertyId changes
  useEffect(() => {
    if (propertyId) {
      fetchAdvancePolicy(propertyId, ratePlanId);
    }
  }, [propertyId, ratePlanId, fetchAdvancePolicy]);

  return {
    config,
    loading,
    error,
    refetch: fetchAdvancePolicy,
  };
};

/**
 * Hook to fetch APPROVAL_RULES config for a property: high_discount_threshold +
 * comp_approval_required. Auto-fetches on propertyId change. Consumers should
 * also call refetch() on modal open/restore so a config change isn't missed.
 */
export const useApprovalRules = (propertyId?: string) => {
  const [config, setConfig] = useState<ApprovalRulesConfig>({
    highDiscountThreshold: 20,
    compApprovalRequired: true,
    roomUpgradeRequiresApproval: false,
    discountApprovalRequired: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchApprovalRules = useCallback(async (propId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getEffectiveConfig(
        'APPROVAL_RULES',
        [{ type: 'PROPERTY', id: propId }]
      );
      const values = response.data;
      const thresholdRaw = values.find((v) => v.configKey === 'high_discount_threshold')?.effectiveValue;
      const compRaw     = values.find((v) => v.configKey === 'comp_approval_required')?.effectiveValue;
      const upgradeRaw  = values.find((v) => v.configKey === 'room_upgrade_requires_approval')?.effectiveValue;
      const discountRaw = values.find((v) => v.configKey === 'discount_approval_required')?.effectiveValue;
      const result: ApprovalRulesConfig = {
        highDiscountThreshold: thresholdRaw ? parseFloat(thresholdRaw) : 20,
        compApprovalRequired: compRaw ? compRaw.toLowerCase() === 'true' : true,
        roomUpgradeRequiresApproval: upgradeRaw ? upgradeRaw.toLowerCase() === 'true' : false,
        // Fail-safe TRUE on missing key — mirrors the server rule.
        discountApprovalRequired: discountRaw ? discountRaw.toLowerCase() !== 'false' : true,
      };
      setConfig(result);
      return result;
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (propertyId) fetchApprovalRules(propertyId);
  }, [propertyId, fetchApprovalRules]);

  return { config, loading, error, refetch: fetchApprovalRules };
};

/**
 * Hook to fetch BLOCKING_POLICY config for a property: the max-hold cap that
 * drives the default expiry on Save & Block, plus the extension trio
 * (block_extension_allowed / max_extensions / extension_hours).
 *
 * Auto-fetches on `propertyId` change. Consumers must also call `refetch()`
 * on modal open/restore — config can change at any time in a multi-user
 * setup, so caching the value across modal sessions is unsafe.
 *
 * Defaults match the seeded values in
 * `crs-db/seed/006_seed_config_definitions.sql` /
 * `migrations/fixes/008_seed_blocked_status_and_lifecycle_config.sql` so
 * the UI degrades sanely when the API call hasn't returned yet.
 */
export const useBlockingPolicy = (propertyId?: string) => {
  const [config, setConfig] = useState<BlockingPolicyConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchBlockingPolicy = useCallback(async (propId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getEffectiveConfig(
        'BLOCKING_POLICY',
        [{ type: 'PROPERTY', id: propId }]
      );
      const values = response.data;
      const durationRaw = values.find((v) => v.configKey === 'block_duration_hours')?.effectiveValue;
      const maxHoldRaw  = values.find((v) => v.configKey === 'max_block_hold_hours')?.effectiveValue;
      const allowedRaw  = values.find((v) => v.configKey === 'block_extension_allowed')?.effectiveValue;
      const maxExtRaw   = values.find((v) => v.configKey === 'max_extensions')?.effectiveValue;
      const extHoursRaw = values.find((v) => v.configKey === 'extension_hours')?.effectiveValue;

      const result: BlockingPolicyConfig = {
        blockDurationHours:    durationRaw ? parseInt(durationRaw, 10) : 24,
        maxBlockHoldHours:     maxHoldRaw  ? parseInt(maxHoldRaw, 10)  : 24,
        blockExtensionAllowed: allowedRaw  ? allowedRaw.toLowerCase() === 'true' : true,
        maxExtensions:         maxExtRaw   ? parseInt(maxExtRaw, 10)   : 2,
        extensionHours:        extHoursRaw ? parseInt(extHoursRaw, 10) : 12,
      };
      setConfig(result);
      return result;
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (propertyId) fetchBlockingPolicy(propertyId);
  }, [propertyId, fetchBlockingPolicy]);

  return { config, loading, error, refetch: fetchBlockingPolicy };
};

/**
 * Hook to fetch the property-level pricing model ('a_la_carte' | 'bundled').
 *
 * Lives under the BOOKING_RULES config group. Auto-fetches when `propertyId`
 * changes. Like the existing policy hooks, consumers are expected to call
 * `refetch()` on modal open/restore because config values can change at any
 * time in a multi-user setup.
 *
 * Returns `'a_la_carte'` whenever the value is missing, empty, or unrecognized
 * (mirrors backend `PricingModels.Normalize`), so callers can always do a
 * simple `=== 'bundled'` comparison.
 */
export const usePricingModel = (propertyId?: string) => {
  const [pricingModel, setPricingModel] = useState<'a_la_carte' | 'bundled'>('a_la_carte');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPricingModel = useCallback(async (propId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getEffectiveConfig(
        'BOOKING_RULES',
        [{ type: 'PROPERTY', id: propId }]
      );
      const raw = response.data.find((v) => v.configKey === 'pricing_model')?.effectiveValue;
      const normalized: 'a_la_carte' | 'bundled' =
        raw?.trim().toLowerCase() === 'bundled' ? 'bundled' : 'a_la_carte';
      setPricingModel(normalized);
      return normalized;
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (propertyId) {
      fetchPricingModel(propertyId);
    } else {
      setPricingModel('a_la_carte');
    }
  }, [propertyId, fetchPricingModel]);

  return { pricingModel, loading, error, refetch: fetchPricingModel };
};
